import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Wallet, RefreshCw, Eye, CheckCircle2, Clock, XCircle, AlertCircle, X,
  FileDown, Send, CircleDollarSign, Pencil, CreditCard, Download, BanknoteIcon,
  ClipboardList, Copy, CheckCheck, Settings, Shield, Timer, Bell, Play, Pause,
  ChevronLeft, ChevronRight, Search, Calendar,
  Package, Truck, RotateCcw, FileSpreadsheet,
  Info, Building2, User, Mail, ExternalLink
} from 'lucide-react';
import './Settlements.css';

const PLATFORM_CURRENCY = 'NGN';

// Date preset helpers
const getDateRange = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  switch (preset) {
    case 'today':
      return { startDate: today.toISOString().split('T')[0], endDate: today.toISOString().split('T')[0] };
    case 'yesterday':
      return { startDate: yesterday.toISOString().split('T')[0], endDate: yesterday.toISOString().split('T')[0] };
    case 'week': {
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - 7);
      return { startDate: weekStart.toISOString().split('T')[0], endDate: today.toISOString().split('T')[0] };
    }
    case 'month': {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: monthStart.toISOString().split('T')[0], endDate: today.toISOString().split('T')[0] };
    }
    case 'year': {
      const yearStart = new Date(today.getFullYear(), 0, 1);
      return { startDate: yearStart.toISOString().split('T')[0], endDate: today.toISOString().split('T')[0] };
    }
    default:
      return { startDate: '', endDate: '' };
  }
};

// Order status progression
const ORDER_STATUS_FLOW = ['PENDING', 'CONFIRMED', 'PROCESSING', 'SHIPPED', 'DELIVERED'];

const ORDER_STATUS_LABELS = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  PROCESSING: 'Processing',
  SHIPPED: 'Shipped',
  OUT_FOR_DELIVERY: 'Out for Delivery',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  REFUNDED: 'Refunded',
  PARTIALLY_REFUNDED: 'Partially Refunded',
  RETURNED: 'Returned'
};

// Expandable settlement flow guide
const SettlementFlowGuide = ({ config }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="stl-guide">
      <button className="stl-guide-toggle" onClick={() => setExpanded(!expanded)}>
        <div className="stl-guide-toggle-left">
          <Info size={16} />
          <span>How Settlements Work</span>
        </div>
        <ChevronRight size={16} className={`stl-guide-chevron ${expanded ? 'expanded' : ''}`} />
      </button>
      {expanded && (
        <div className="stl-guide-body">
          <div className="stl-guide-section">
            <h4>Settlement Lifecycle</h4>
            <div className="stl-guide-flow">
              <span className="stl-guide-step">Payment Received</span>
              <span className="stl-guide-arrow">&rarr;</span>
              <span className="stl-guide-step stl-guide-step-hold">On Hold ({config.holdPeriodHours}h)</span>
              <span className="stl-guide-arrow">&rarr;</span>
              <span className="stl-guide-step stl-guide-step-pending">Pending Review</span>
              <span className="stl-guide-arrow">&rarr;</span>
              <span className="stl-guide-step stl-guide-step-processing">Processing</span>
              <span className="stl-guide-arrow">&rarr;</span>
              <span className="stl-guide-step stl-guide-step-completed">Paid Out</span>
            </div>
          </div>

          <div className="stl-guide-columns">
            <div className="stl-guide-section">
              <h4>Conditions Before Payout</h4>
              <ul>
                <li>{config.holdEnabled ? `Hold period of ${config.holdPeriodHours} hours must expire` : 'Hold period is disabled — settlements go straight to Pending'}</li>
                <li>Release condition: <strong>{config.releaseCondition === 'TIME_OR_DELIVERED' ? 'Hold expires OR order delivered (whichever first)' : config.releaseCondition === 'ORDER_DELIVERED' ? 'Order must be marked as Delivered' : `${config.holdPeriodHours} hours must pass`}</strong></li>
                <li>Admin must approve (move to Payment Queue){config.autoProcessEnabled ? ` — or auto-approved if ≤ ₦${config.autoApprovalLimit?.toLocaleString()}` : ''}</li>
                <li>Merchant must have approved KYC and verified bank account</li>
                <li>Transaction fee is deducted from the order amount when the merchant pays it</li>
              </ul>
            </div>

            <div className="stl-guide-section">
              <h4>Payout Methods</h4>
              <ul>
                <li><strong>Auto Payout</strong> — Transfers funds to merchant's bank via Paystack API. Instant settlement.</li>
                <li><strong>Manual Payout</strong> — Admin performs a bank transfer externally, then records the reference here via "Mark as Paid".</li>
              </ul>
              <h4>Current Settings</h4>
              <ul>
                <li>Hold: <strong>{config.holdEnabled ? `Enabled (${config.holdPeriodHours}h / ${Math.floor(config.holdPeriodHours / 24)}d ${config.holdPeriodHours % 24}h)` : 'Disabled'}</strong></li>
                <li>Auto-release: <strong>{config.autoReleaseEnabled ? 'On' : 'Off'}</strong> (cron runs every 30 min)</li>
                <li>Auto-process: <strong>{config.autoProcessEnabled ? `On (up to ₦${config.autoApprovalLimit?.toLocaleString()})` : 'Off — manual approval required'}</strong></li>
                <li>Merchant notifications: Created {config.notifyMerchantOnCreated ? '✓' : '✗'} | Hold {config.notifyMerchantOnHold ? '✓' : '✗'} | Release {config.notifyMerchantOnRelease ? '✓' : '✗'} | Adjusted {config.notifyMerchantOnAdjusted ? '✓' : '✗'} | Failed {config.notifyMerchantOnFailed ? '✓' : '✗'} | Paid Out {config.notifyMerchantOnPaidOut ? '✓' : '✗'}</li>
              </ul>
            </div>
          </div>

          <div className="stl-guide-section stl-guide-note">
            <strong>Note:</strong> Settlement creation triggers include: Paystack/Flutterwave payment webhooks, DVA (bank transfer) webhooks, and merchant manual settlement requests. Transaction fee rules vary by merchant tier and channel.
          </div>
        </div>
      )}
    </div>
  );
};

const Settlements = () => {
  const [settlements, setSettlements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [transferDate, setTransferDate] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [activeView, setActiveView] = useState('all');
  const [datePreset, setDatePreset] = useState('all');
  const [merchantSearch, setMerchantSearch] = useState('');
  const [debouncedMerchantSearch, setDebouncedMerchantSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [payoutProvider, setPayoutProvider] = useState('PAYSTACK');
  const exportMenuRef = useRef(null);
  const [settlementConfig, setSettlementConfig] = useState({
    holdEnabled: true,
    holdPeriodHours: 48,
    releaseCondition: 'TIME_OR_DELIVERED',
    autoReleaseEnabled: true,
    autoProcessEnabled: false,
    autoApprovalLimit: 50000,
    notifyMerchantOnHold: true,
    notifyMerchantOnRelease: true,
    notifyMerchantOnCreated: true,
    notifyMerchantOnAdjusted: true,
    notifyMerchantOnFailed: true,
    notifyMerchantOnPaidOut: true,
    notifyAdminOnHold: false
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [gatewayBalances, setGatewayBalances] = useState(null);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [payoutError, setPayoutError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    merchantId: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  // Close export menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-dismiss messages
  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(t);
    }
  }, [error]);
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(t);
    }
  }, [success]);

  // Debounce merchantSearch for server-side API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMerchantSearch(merchantSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [merchantSearch]);

  const loadSettlements = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      let statusFilter = filters.status;
      if (activeView === 'payment-queue' && !filters.status) statusFilter = 'PROCESSING';
      else if (activeView === 'completed' && !filters.status) statusFilter = 'COMPLETED';
      else if (activeView === 'on-hold' && !filters.status) statusFilter = 'ON_HOLD';

      const queryParams = new URLSearchParams({
        page: pagination.current,
        limit: 20
      });
      if (statusFilter) queryParams.set('status', statusFilter);
      if (filters.startDate) queryParams.set('startDate', filters.startDate);
      if (filters.endDate) queryParams.set('endDate', filters.endDate);
      if (filters.merchantId) queryParams.set('merchantId', filters.merchantId);
      if (debouncedMerchantSearch.trim()) queryParams.set('merchantSearch', debouncedMerchantSearch.trim());
      if (providerFilter) queryParams.set('provider', providerFilter);

      const response = await fetch(`/api/admin/settlements?${queryParams}`, { credentials: 'include' });
      const data = await response.json();

      if (data.success) {
        setSettlements(data.settlements || []);
        if (data.pagination) {
          setPagination(prev => ({
            ...prev,
            pages: data.pagination.pages || 1,
            total: data.pagination.total || 0
          }));
        }
      } else {
        setError(data.error || 'Failed to load settlements');
      }
    } catch (err) {
      setError('Failed to load settlements');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeView, filters, pagination.current, debouncedMerchantSearch, providerFilter]);

  const loadStats = async () => {
    try {
      const response = await fetch('/api/admin/settlements/stats', { credentials: 'include' });
      const data = await response.json();
      if (data.success) setStats(data.stats);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadSettlementConfig = async () => {
    try {
      setConfigLoading(true);
      const response = await fetch('/api/admin/settlements/config', { credentials: 'include' });
      const data = await response.json();
      if (data.success && data.config) setSettlementConfig(data.config);
    } catch (err) {
      console.error('Failed to load settlement config:', err);
    } finally {
      setConfigLoading(false);
    }
  };

  const saveSettlementConfig = async () => {
    try {
      setConfigLoading(true);
      const response = await fetch('/api/admin/settlements/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settlementConfig)
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Settlement configuration saved successfully');
        setSettlementConfig(data.config);
      } else {
        setError(data.error || 'Failed to save configuration');
      }
    } catch (err) {
      setError('Failed to save configuration');
    } finally {
      setConfigLoading(false);
    }
  };

  const loadGatewayBalances = async () => {
    setBalancesLoading(true);
    try {
      const response = await fetch('/api/admin/settlements/gateway-balances', { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setGatewayBalances(data.balances);
      }
    } catch (err) {
      console.error('Failed to load gateway balances:', err);
    } finally {
      setBalancesLoading(false);
    }
  };

  // Prompt actions
  const promptApprove = (id) => {
    setConfirmAction({
      id, action: 'approve',
      title: 'Approve Settlement',
      message: 'Move this settlement to the Payment Queue for processing.',
      confirmText: 'Approve', confirmClass: 'stl-btn-success'
    });
    setActionNotes('');
    setShowConfirmDialog(true);
  };

  const promptPayout = (settlement) => {
    // Detect the payment provider from settlement metadata or default to PAYSTACK
    const detectedProvider = settlement.metadata?.paymentProvider?.toUpperCase() || 'PAYSTACK';
    setPayoutProvider(detectedProvider);

    setConfirmAction({
      id: settlement.id, action: 'payout',
      title: 'Process Auto Payout',
      message: `Initiate an automatic bank transfer of ${formatCurrency(settlement.merchantAmount)} to ${settlement.merchant?.store?.name || settlement.merchant?.username || 'merchant'}.`,
      confirmText: 'Process Payout', confirmClass: 'stl-btn-primary', settlement
    });
    setActionNotes('');
    setShowConfirmDialog(true);
  };

  const promptHold = (id) => {
    setConfirmAction({
      id, action: 'hold',
      title: 'Hold Settlement',
      message: 'Place this settlement on hold. A reason is required.',
      confirmText: 'Hold Settlement', confirmClass: 'stl-btn-warning'
    });
    setActionNotes('');
    setShowConfirmDialog(true);
  };

  const promptRelease = (id) => {
    setConfirmAction({
      id, action: 'release',
      title: 'Release Settlement',
      message: 'Move this settlement from On Hold back to Pending review.',
      confirmText: 'Release Settlement', confirmClass: 'stl-btn-success'
    });
    setActionNotes('');
    setShowConfirmDialog(true);
  };

  const promptAdjust = (settlement) => {
    setConfirmAction({
      id: settlement.id, action: 'adjust',
      title: 'Adjust Settlement Amount',
      message: `Current payout amount: ${formatCurrency(settlement.merchantAmount)}`,
      confirmText: 'Adjust Amount', confirmClass: 'stl-btn-primary', settlement
    });
    setAdjustAmount(settlement.merchantAmount?.toString() || '');
    setActionNotes('');
    setShowConfirmDialog(true);
  };

  const promptMarkPaid = (settlement) => {
    setConfirmAction({
      id: settlement.id, action: 'mark-paid',
      title: 'Mark as Paid',
      message: `Confirm manual bank transfer of ${formatCurrency(settlement.merchantAmount)} has been completed.`,
      confirmText: 'Confirm Payment', confirmClass: 'stl-btn-success', settlement
    });
    setBankReference('');
    setTransferDate(new Date().toISOString().split('T')[0]);
    setActionNotes('');
    setShowConfirmDialog(true);
  };

  const executeAction = async () => {
    if (!confirmAction || processing) return;
    setProcessing(true);
    const { id, ids, action } = confirmAction;

    try {
      if (action === 'bulk-approve') {
        const results = await Promise.all(
          ids.map(itemId =>
            fetch(`/api/admin/settlements/${itemId}/approve`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ adminNotes: actionNotes || 'Bulk approved', payoutDays: 0 })
            }).then(r => r.json())
          )
        );
        const successCount = results.filter(r => r.success).length;
        setSuccess(`${successCount} of ${ids.length} settlements approved`);
        setSelectedItems([]);
      } else if (action === 'approve') {
        const response = await fetch(`/api/admin/settlements/${id}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ adminNotes: actionNotes || 'Approved', payoutDays: 0 })
        });
        const data = await response.json();
        if (data.success) setSuccess('Settlement approved and moved to Payment Queue');
        else setError(data.error || 'Failed to approve');
      } else if (action === 'payout') {
        const response = await fetch(`/api/admin/settlements/${id}/payout-gateway`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ test: false, forceProvider: payoutProvider })
        });
        const data = await response.json();
        if (data.success) {
          setSuccess(`Payout processed successfully via ${payoutProvider}`);
          setPayoutError(null);
        } else {
          // Check if this is an insufficient balance error with details
          if (data.code === 'INSUFFICIENT_BALANCE' && data.details) {
            setPayoutError({ code: data.code, details: data.details, error: data.error });
            setError(`Insufficient balance: ${data.details.message}`);
          } else {
            setPayoutError(null);
            setError(data.error || 'Failed to process payout');
          }
          // Don't close the dialog on insufficient balance error
          if (data.code === 'INSUFFICIENT_BALANCE') {
            setProcessing(false);
            return;
          }
        }
      } else if (action === 'hold') {
        if (!actionNotes.trim()) { setError('Please provide a reason'); setProcessing(false); return; }
        const response = await fetch(`/api/admin/settlements/${id}/hold`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ reason: actionNotes, adminNotes: actionNotes })
        });
        const data = await response.json();
        if (data.success) setSuccess('Settlement placed on hold');
        else setError(data.error || 'Failed to hold');
      } else if (action === 'release') {
        const response = await fetch(`/api/admin/settlements/${id}/release`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ note: actionNotes.trim() || 'Released from admin settlements queue' })
        });
        const data = await response.json();
        if (data.success) setSuccess('Settlement released and moved to Pending');
        else setError(data.error || 'Failed to release');
      } else if (action === 'adjust') {
        if (!adjustAmount || parseFloat(adjustAmount) <= 0) { setError('Enter a valid amount'); setProcessing(false); return; }
        if (!actionNotes.trim()) { setError('Please provide a reason'); setProcessing(false); return; }
        const response = await fetch(`/api/admin/settlements/${id}/adjust`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ newAmount: parseFloat(adjustAmount), reason: actionNotes })
        });
        const data = await response.json();
        if (data.success) setSuccess('Settlement amount adjusted');
        else setError(data.error || 'Failed to adjust');
      } else if (action === 'mark-paid') {
        if (!bankReference.trim()) { setError('Enter bank transfer reference'); setProcessing(false); return; }
        const response = await fetch(`/api/admin/settlements/${id}/mark-paid`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ bankReference: bankReference.trim(), transferDate: transferDate || new Date().toISOString(), notes: actionNotes })
        });
        const data = await response.json();
        if (data.success) setSuccess('Settlement marked as paid');
        else setError(data.error || 'Failed to mark as paid');
      }

      loadSettlements();
      loadStats();
      setShowConfirmDialog(false);
      setConfirmAction(null);
      setActionNotes('');
      setAdjustAmount('');
      setBankReference('');
      setTransferDate('');
    } catch (err) {
      setError(`Action failed: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const cancelAction = () => {
    setShowConfirmDialog(false);
    setConfirmAction(null);
    setActionNotes('');
    setPayoutError(null);
  };

  const toggleSelectItem = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = (items) => {
    if (selectedItems.length === items.length) setSelectedItems([]);
    else setSelectedItems(items.map(i => i.id));
  };

  const handleBulkApprove = () => {
    if (selectedItems.length === 0) { setError('Select settlements to approve'); return; }
    setConfirmAction({
      ids: selectedItems, action: 'bulk-approve',
      title: `Approve ${selectedItems.length} Settlement${selectedItems.length > 1 ? 's' : ''}`,
      message: `Move ${selectedItems.length} settlements to the Payment Queue?`,
      confirmText: 'Approve All', confirmClass: 'stl-btn-success'
    });
    setActionNotes('');
    setShowConfirmDialog(true);
  };

  // Export functions
  const generateCSV = (data, headers, filename) => {
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join(','),
      ...data.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportForPayment = () => {
    const items = filteredSettlements.filter(s => s.settlementStatus === 'PROCESSING');
    if (items.length === 0) { setError('No settlements in Payment Queue to export'); return; }

    const headers = ['Settlement ID', 'Merchant Name', 'Merchant Email', 'Store Name', 'Bank Name', 'Account Number', 'Account Name', 'Bank Code', 'Amount (NGN)', 'Order ID', 'Order Status', 'Created Date'];
    const data = items.map(s => {
      const bd = s.merchantBankDetails || s.merchant?.bankDetails || {};
      return [
        s.id,
        s.merchant?.username || 'N/A',
        s.merchant?.email || 'N/A',
        s.merchant?.store?.name || s.storeName || 'N/A',
        bd.bankName || bd.bank_name || 'N/A',
        bd.accountNumber || bd.account_number || 'N/A',
        bd.accountName || bd.account_name || 'N/A',
        bd.bankCode || bd.bank_code || '',
        s.merchantAmount || 0,
        s.orderId || 'N/A',
        s.order?.status || 'N/A',
        new Date(s.createdAt).toLocaleString()
      ];
    });

    generateCSV(data, headers, `payment-queue-${new Date().toISOString().split('T')[0]}.csv`);
    setSuccess(`Exported ${items.length} settlements for payment`);
  };

  const exportAllCSV = () => {
    if (filteredSettlements.length === 0) { setError('No settlements to export'); return; }

    const headers = ['Settlement ID', 'Merchant', 'Email', 'Store', 'Order ID', 'Order Status', 'Order Amount', 'Transaction Fee', 'Processor Recovery', 'Revstar Fee', 'Platform VAT', 'Fee Model', 'Who Pays', 'Transaction Fee Rate', 'Merchant Payout', 'Settlement Status', 'Settlement Method', 'Bank Reference', 'Created Date', 'Processed Date', 'Hold Until', 'Failure Reason', 'Fraud Flags'];
    const data = filteredSettlements.map(s => {
      const split = getFeeSplit(s);
      return [
        s.id,
        s.merchant?.username || s.merchant?.email || 'N/A',
        s.merchant?.email || 'N/A',
        s.merchant?.store?.name || s.storeName || 'N/A',
        s.orderId || 'N/A',
        s.order?.status || 'N/A',
        s.totalAmount || 0,
        split.total || s.serviceCharge || 0,
        split.processorRecovery || 0,
        split.platformFee || 0,
        split.platformVat || 0,
        split.modelVersion ? `v${split.modelVersion}` : 'legacy',
        split.whoPays || s.serviceChargeWhoPays || s.order?.serviceChargeWhoPays || '',
        s.serviceChargeRate || 0,
        s.merchantAmount || 0,
        s.settlementStatus || 'PENDING',
        s.settlementMethod || 'N/A',
        s.settlementReference || '',
        s.createdAt ? new Date(s.createdAt).toLocaleString() : '',
        s.processedAt ? new Date(s.processedAt).toLocaleString() : '',
        s.holdUntil ? new Date(s.holdUntil).toLocaleString() : '',
        s.failureReason || '',
        (s.fraudFlags || []).join('; ')
      ];
    });

    // Summary row
    const totalPayout = filteredSettlements.reduce((sum, s) => sum + parseFloat(s.merchantAmount || 0), 0);
    const totalOrder = filteredSettlements.reduce((sum, s) => sum + parseFloat(s.totalAmount || 0), 0);
    const totalCharge = filteredSettlements.reduce((sum, s) => sum + (getFeeSplit(s).total || parseFloat(s.serviceCharge || 0)), 0);
    const totalProcessor = filteredSettlements.reduce((sum, s) => sum + getFeeSplit(s).processorRecovery, 0);
    const totalPlatformFee = filteredSettlements.reduce((sum, s) => sum + getFeeSplit(s).platformFee, 0);
    const totalPlatformVat = filteredSettlements.reduce((sum, s) => sum + getFeeSplit(s).platformVat, 0);
    data.push([]);
    data.push(['SUMMARY', '', '', '', '', '', totalOrder.toFixed(2), totalCharge.toFixed(2), totalProcessor.toFixed(2), totalPlatformFee.toFixed(2), totalPlatformVat.toFixed(2), '', '', '', totalPayout.toFixed(2), `${filteredSettlements.length} settlements`, '', '', `Generated: ${new Date().toLocaleString()}`, '', '', '', '']);

    const statusLabel = activeView === 'payment-queue' ? 'payment-queue' : activeView === 'completed' ? 'completed' : activeView === 'on-hold' ? 'on-hold' : 'all';
    generateCSV(data, headers, `settlements-${statusLabel}-${new Date().toISOString().split('T')[0]}.csv`);
    setSuccess('Settlements exported successfully');
  };

  const exportExcel = () => {
    // Export as tab-separated values with .xls extension for Excel compatibility
    if (filteredSettlements.length === 0) { setError('No settlements to export'); return; }

    const headers = ['Settlement ID', 'Merchant', 'Email', 'Store', 'Order ID', 'Order Status', 'Order Amount', 'Transaction Fee', 'Processor Recovery', 'Revstar Fee', 'Platform VAT', 'Merchant Payout', 'Settlement Status', 'Created Date', 'Processed Date'];
    const data = filteredSettlements.map(s => {
      const split = getFeeSplit(s);
      return [
        s.id,
        s.merchant?.username || 'N/A',
        s.merchant?.email || 'N/A',
        s.merchant?.store?.name || s.storeName || 'N/A',
        s.orderId || 'N/A',
        s.order?.status || 'N/A',
        s.totalAmount || 0,
        split.total || s.serviceCharge || 0,
        split.processorRecovery || 0,
        split.platformFee || 0,
        split.platformVat || 0,
        s.merchantAmount || 0,
        s.settlementStatus || 'PENDING',
        s.createdAt ? new Date(s.createdAt).toLocaleString() : '',
        s.processedAt ? new Date(s.processedAt).toLocaleString() : ''
      ];
    });

    const tsv = [headers.join('\t'), ...data.map(row => row.join('\t'))].join('\n');
    const blob = new Blob(['\uFEFF' + tsv], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `settlements-${new Date().toISOString().split('T')[0]}.xls`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSuccess('Exported to Excel format');
  };

  const copyBankDetails = (settlement) => {
    const bd = settlement.merchantBankDetails || settlement.merchant?.bankDetails || {};
    const text = `Bank: ${bd.bankName || bd.bank_name || 'N/A'}\nAccount: ${bd.accountNumber || bd.account_number || 'N/A'}\nName: ${bd.accountName || bd.account_name || 'N/A'}\nAmount: ${formatCurrency(settlement.merchantAmount)}`;
    navigator.clipboard.writeText(text);
    setCopiedId(settlement.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const viewDetails = (item) => { setSelectedItem(item); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setSelectedItem(null); };

  // Date preset handler
  const handleDatePreset = (preset) => {
    setDatePreset(preset);
    if (preset === 'all') {
      setFilters(prev => ({ ...prev, startDate: '', endDate: '' }));
    } else if (preset === 'custom') {
      // Keep current dates, user will pick
    } else {
      const range = getDateRange(preset);
      setFilters(prev => ({ ...prev, ...range }));
    }
  };

  const clearFilters = () => {
    setFilters({ status: '', merchantId: '', startDate: '', endDate: '' });
    setMerchantSearch('');
    setOrderStatusFilter('');
    setProviderFilter('');
    setDatePreset('all');
  };

  // Client-side filtering for order status (merchant search is now server-side)
  const filteredSettlements = useMemo(() => {
    if (!orderStatusFilter) return settlements;
    return settlements.filter(s => {
      const orderStatus = (s.order?.status || '').toUpperCase();
      return orderStatus === orderStatusFilter.toUpperCase();
    });
  }, [settlements, orderStatusFilter]);

  useEffect(() => { loadStats(); loadSettlementConfig(); loadGatewayBalances(); }, []);
  useEffect(() => { setPagination(prev => ({ ...prev, current: 1 })); }, [activeView, filters.status, filters.startDate, filters.endDate, debouncedMerchantSearch, providerFilter]);
  useEffect(() => { loadSettlements(); setSelectedItems([]); }, [loadSettlements]);

  // Helpers
  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED': return <CheckCircle2 size={14} />;
      case 'PENDING': return <Clock size={14} />;
      case 'PROCESSING': return <RefreshCw size={14} className="stl-spinner" />;
      case 'FAILED': return <XCircle size={14} />;
      case 'ON_HOLD': return <Shield size={14} />;
      default: return <AlertCircle size={14} />;
    }
  };

  const getOrderStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'DELIVERED': return '#059669';
      case 'SHIPPED': case 'OUT_FOR_DELIVERY': return '#3b82f6';
      case 'PROCESSING': case 'CONFIRMED': return '#f59e0b';
      case 'PENDING': return '#6b7280';
      case 'CANCELLED': return '#dc2626';
      case 'REFUNDED': case 'PARTIALLY_REFUNDED': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const isSettlementBlockedByOrderStatus = (settlement) => (
    ['CANCELLED', 'REFUNDED', 'PARTIALLY_REFUNDED'].includes(settlement?.order?.status?.toUpperCase())
  );

  const formatCurrency = (amount, currency = PLATFORM_CURRENCY) => {
    try {
      return new Intl.NumberFormat('en-NG', { style: 'currency', currency }).format(amount || 0);
    } catch (e) {
      return `${currency} ${(amount || 0).toLocaleString()}`;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatShortDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-NG', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const moneyValue = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const valuesClose = (a, b) => Math.abs(moneyValue(a) - moneyValue(b)) < 1;

  const firstMoney = (...values) => {
    for (const value of values) {
      const parsed = moneyValue(value);
      if (parsed > 0) return parsed;
    }
    return 0;
  };

  const getPlatformFeeQuote = (source = {}) => (
    source.metadata?.platformFeeQuote
    || source.metadata?.platformFee?.result
    || source.metadata?.platformFee
    || null
  );

  const getFeeComponent = (source = {}, key, quoteKey, breakdownKey = quoteKey) => {
    const quote = getPlatformFeeQuote(source) || {};
    const breakdown = source.feeBreakdown || quote.breakdown || {};
    return firstMoney(
      source[key],
      quote[quoteKey],
      breakdown[breakdownKey]?.amount
    );
  };

  const getProfitability = (settlement = {}) => {
    const order = settlement.order || {};
    return settlement.feeBreakdown?.profitability
      || settlement.metadata?.profitability
      || settlement.metadata?.feeBreakdown?.profitability
      || order.feeBreakdown?.profitability
      || order.metadata?.profitability
      || order.metadata?.feeBreakdown?.profitability
      || getPlatformFeeQuote(settlement)?.breakdown?.profitability
      || getPlatformFeeQuote(order)?.breakdown?.profitability
      || null;
  };

  const getFeeSplit = (settlement = {}) => {
    const order = settlement.order || {};
    const processorRecovery = getFeeComponent(settlement, 'processorRecoveryAmount', 'processorRecovery')
      || getFeeComponent(order, 'processorRecoveryAmount', 'processorRecovery');
    const platformFee = getFeeComponent(settlement, 'platformFeeAmount', 'platformFee')
      || getFeeComponent(order, 'platformFeeAmount', 'platformFee');
    const platformVat = getFeeComponent(settlement, 'platformFeeVatAmount', 'platformVat')
      || getFeeComponent(order, 'platformFeeVatAmount', 'platformVat');
    const directTotal = processorRecovery + platformFee + platformVat;
    const aggregateTransactionFee = moneyValue(settlement.serviceCharge) || moneyValue(order.serviceCharge);
    const total = directTotal > 0 ? directTotal : aggregateTransactionFee;
    const hasSplit = directTotal > 0 || Boolean(settlement.feeModelVersion || order.feeModelVersion || getPlatformFeeQuote(settlement) || getPlatformFeeQuote(order));

    return {
      processorRecovery,
      platformFee,
      platformVat,
      total,
      hasSplit,
      modelVersion: settlement.feeModelVersion || order.feeModelVersion || getPlatformFeeQuote(settlement)?.modelVersion || getPlatformFeeQuote(order)?.modelVersion || null,
      whoPays: settlement.feeWhoPays || order.feeWhoPays || settlement.serviceChargeWhoPays || order.serviceChargeWhoPays || null,
      provider: settlement.processorProvider || order.processorProvider || getPlatformFeeQuote(settlement)?.processorProvider || getPlatformFeeQuote(order)?.processorProvider || null,
      profitability: getProfitability(settlement)
    };
  };

  const getSettlementBreakdown = (settlement) => {
    const order = settlement?.order || {};
    const subtotal = moneyValue(order.subtotal);
    const vat = moneyValue(order.taxTotal);
    const delivery = moneyValue(order.shippingFee);
    const orderServiceCharge = moneyValue(order.serviceCharge);
    const settlementServiceCharge = moneyValue(settlement?.serviceCharge);
    const feeSplit = getFeeSplit(settlement);
    const serviceCharge = feeSplit.total || orderServiceCharge || settlementServiceCharge;
    const storedMerchantAmount = moneyValue(settlement?.merchantAmount);
    const storedOrderTotal = moneyValue(settlement?.totalAmount);
    const orderTotal = moneyValue(order.total) || storedOrderTotal;
    const reconstructedTotal = subtotal + vat + delivery;
    const hasOrderParts = subtotal > 0 || vat > 0 || delivery > 0;
    const customerPaidTotal = hasOrderParts && valuesClose(orderTotal, reconstructedTotal)
      ? reconstructedTotal
      : orderTotal;
    const serviceChargeDeductedFromPayout = serviceCharge > 0 && valuesClose(storedMerchantAmount, customerPaidTotal - serviceCharge);
    const serviceChargeLabel = serviceChargeDeductedFromPayout
      ? 'Transaction fee withheld from settlement'
      : 'Transaction fee tracked separately';

    return {
      subtotal,
      vat,
      delivery,
      customerPaidTotal,
      serviceCharge,
      serviceChargeLabel,
      serviceChargeDeductedFromPayout,
      feeSplit,
      merchantPayout: storedMerchantAmount,
      hasOrderParts
    };
  };

  const getSettlementStatusReason = (settlement) => {
    const s = settlement.settlementStatus;
    if (s === 'PENDING') return 'Awaiting admin review and approval';
    if (s === 'PROCESSING') return 'Approved and queued for bank transfer';
    if (s === 'COMPLETED') {
      if (settlement.metadata?.manualPayment) return `Paid via manual transfer (Ref: ${settlement.metadata?.bankTransferReference || 'N/A'})`;
      return 'Payout completed successfully';
    }
    if (s === 'FAILED') return settlement.failureReason || 'Bank transfer failed';
    if (s === 'ON_HOLD') {
      const holdReason = settlement.metadata?.holdReason || settlement.metadata?.reason || '';
      if (settlement.holdUntil) return `On hold until ${formatDate(settlement.holdUntil)}${holdReason ? `. Reason: ${holdReason}` : ''}`;
      return holdReason || 'Placed on hold by admin';
    }
    return '';
  };

  const pendingCount = stats?.pendingCount || 0;
  const processingCount = stats?.processingCount || 0;
  const completedCount = stats?.completedCount || 0;
  const onHoldCount = stats?.onHoldCount || 0;
  const failedCount = stats?.failedCount || 0;

  // Order status timeline for detail modal
  const renderOrderTimeline = (order) => {
    if (!order) return null;
    const currentStatus = (order.status || 'PENDING').toUpperCase();
    const isCancelled = currentStatus === 'CANCELLED';
    const isRefunded = ['REFUNDED', 'PARTIALLY_REFUNDED'].includes(currentStatus);

    // Build timeline steps
    const steps = isCancelled || isRefunded
      ? [...ORDER_STATUS_FLOW.slice(0, ORDER_STATUS_FLOW.indexOf(currentStatus) > -1 ? ORDER_STATUS_FLOW.indexOf(currentStatus) : 1), currentStatus]
      : ORDER_STATUS_FLOW;

    const currentIdx = steps.indexOf(currentStatus);

    // Build a map of status -> timestamp from statusHistory if available
    const historyEntries = Array.isArray(order.statusHistory) ? order.statusHistory : [];
    const statusTimestamps = {};
    historyEntries.forEach(entry => {
      const st = (entry.status || '').toUpperCase();
      if (st && entry.timestamp) {
        statusTimestamps[st] = entry.timestamp;
      }
    });

    const formatShortDateTimeline = (ts) => {
      try {
        const d = new Date(ts);
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ', ' +
               d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      } catch { return ''; }
    };

    return (
      <div className="stl-order-timeline">
        {steps.map((step, idx) => {
          const isPast = idx < currentIdx;
          const isCurrent = idx === currentIdx;
          const isFuture = idx > currentIdx;
          const isError = isCurrent && (isCancelled || isRefunded);
          const timestamp = statusTimestamps[step];

          return (
            <div key={step} className={`stl-timeline-step ${isPast ? 'past' : ''} ${isCurrent ? 'current' : ''} ${isFuture ? 'future' : ''} ${isError ? 'error' : ''}`}>
              <div className="stl-timeline-dot">
                {(isPast || isCurrent) && !isError && (
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4"><polyline points="20 6 9 17 4 12" /></svg>
                )}
                {isError && (
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                )}
              </div>
              {idx < steps.length - 1 && <div className="stl-timeline-line" />}
              <div className="stl-timeline-label">
                {ORDER_STATUS_LABELS[step] || step}
              </div>
              {timestamp && (isPast || isCurrent) && (
                <div className="stl-timeline-time">{formatShortDateTimeline(timestamp)}</div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const pendingItems = filteredSettlements.filter(s => s.settlementStatus === 'PENDING');

  const tableTitle = activeView === 'payment-queue' ? 'Payment Queue' :
    activeView === 'completed' ? 'Paid Out' :
    activeView === 'on-hold' ? 'On Hold' : 'All Settlements';

  const isPaymentQueue = activeView === 'payment-queue';
  const tableColSpan = isPaymentQueue ? 8 : 7;

  return (
    <div className="stl-container">
      {/* Toast Messages */}
      {error && (
        <div className="stl-toast stl-toast-error">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="stl-toast-close"><X size={16} /></button>
        </div>
      )}
      {success && (
        <div className="stl-toast stl-toast-success">
          <CheckCircle2 size={18} />
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="stl-toast-close"><X size={16} /></button>
        </div>
      )}

      {/* Page Header */}
      <div className="stl-header">
        <div className="stl-header-left">
          <div className="stl-header-icon-wrap">
            <Wallet size={24} />
          </div>
          <div>
            <h1>Settlements</h1>
            <p>Manage merchant payouts and track settlement statuses</p>
          </div>
        </div>
        <div className="stl-header-actions">
          <button onClick={() => { loadStats(); loadSettlements(); }} className="stl-btn stl-btn-secondary">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Settlement Flow Guide */}
      <SettlementFlowGuide config={settlementConfig} />

      {/* Stats Cards — 3 Hero Numbers */}
      <div className="stl-stats-grid">
        <div className={`stl-stat-card ${activeView === 'all' && filters.status === 'PENDING' ? 'active' : ''}`} onClick={() => { setActiveView('all'); setFilters(prev => ({ ...prev, status: 'PENDING' })); }}>
          <span className="stl-stat-label">Pending Payout</span>
          <span className="stl-stat-value">{pendingCount}</span>
          <span className="stl-stat-amount">{formatCurrency(stats?.pendingAmount || 0)}</span>
        </div>
        <div className={`stl-stat-card ${activeView === 'payment-queue' ? 'active' : ''}`} onClick={() => setActiveView('payment-queue')}>
          <span className="stl-stat-label">Processing</span>
          <span className="stl-stat-value">{processingCount}</span>
          <span className="stl-stat-amount">{formatCurrency(stats?.processingAmount || 0)}</span>
        </div>
        <div className={`stl-stat-card ${activeView === 'completed' ? 'active' : ''}`} onClick={() => setActiveView('completed')}>
          <span className="stl-stat-label">Paid Out</span>
          <span className="stl-stat-value">{completedCount}</span>
          <span className="stl-stat-amount">{formatCurrency(stats?.completedAmount || 0)}</span>
        </div>
      </div>

      {/* Gateway Balances */}
      <div className="stl-gateway-balances">
        <div className="stl-balance-header">
          <h3>Gateway Balances</h3>
          <button onClick={loadGatewayBalances} disabled={balancesLoading} className="stl-btn stl-btn-ghost stl-btn-sm">
            <RefreshCw size={14} className={balancesLoading ? 'stl-spinner' : ''} />
            Refresh
          </button>
        </div>
        <div className="stl-balance-cards">
          {/* Paystack */}
          <div className="stl-balance-card stl-balance-paystack">
            <div className="stl-balance-card-header">
              <span className="stl-provider-badge stl-provider-paystack">Paystack</span>
              {gatewayBalances?.paystack?.success === false && (
                <span className="stl-balance-error-badge" title={gatewayBalances.paystack.error}>Error</span>
              )}
            </div>
            <div className="stl-balance-amount">
              {balancesLoading ? '...' : formatCurrency(gatewayBalances?.paystack?.availableBalance || 0)}
            </div>
            <div className="stl-balance-label">Available Balance</div>
            {gatewayBalances?.paystack?.scheduleInfo && (
              <div className="stl-balance-schedule">
                <small>
                  <strong>Settlement:</strong> {gatewayBalances.paystack.scheduleInfo.localSettlement}<br />
                  <strong>Payout:</strong> {gatewayBalances.paystack.scheduleInfo.payoutTime}<br />
                  <em>{gatewayBalances.paystack.scheduleInfo.weekends}</em>
                </small>
              </div>
            )}
          </div>

          {/* Flutterwave */}
          <div className="stl-balance-card stl-balance-flutterwave">
            <div className="stl-balance-card-header">
              <span className="stl-provider-badge stl-provider-flutterwave">Flutterwave</span>
              {gatewayBalances?.flutterwave?.success === false && (
                <span className="stl-balance-error-badge" title={gatewayBalances.flutterwave.error}>Error</span>
              )}
            </div>
            <div className="stl-balance-amount">
              {balancesLoading ? '...' : formatCurrency(gatewayBalances?.flutterwave?.availableBalance || 0)}
            </div>
            <div className="stl-balance-label">Payout Balance</div>
            {gatewayBalances?.flutterwave?.ledgerBalance > 0 && (
              <div className="stl-balance-pending">
                Ledger: {formatCurrency(gatewayBalances.flutterwave.ledgerBalance)}
              </div>
            )}
            {gatewayBalances?.flutterwave?.scheduleInfo && (
              <div className="stl-balance-schedule">
                <small>
                  <strong>Local:</strong> {gatewayBalances.flutterwave.scheduleInfo.localSettlement}<br />
                  <strong>International:</strong> {gatewayBalances.flutterwave.scheduleInfo.internationalSettlement}
                </small>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Combined Toolbar: Tabs + Filters */}
      <div className="stl-toolbar">
        {/* Left side: tabs */}
        <div className="stl-tabs">
          <button onClick={() => { setActiveView('all'); setFilters(prev => ({ ...prev, status: '' })); }}
            className={`stl-tab ${activeView === 'all' && !filters.status ? 'active' : ''}`}>All</button>
          <button onClick={() => setActiveView('payment-queue')}
            className={`stl-tab ${activeView === 'payment-queue' ? 'active' : ''}`}>
            Payment Queue {processingCount > 0 && <span className="stl-tab-badge">{processingCount}</span>}
          </button>
          <button onClick={() => setActiveView('completed')}
            className={`stl-tab ${activeView === 'completed' ? 'active' : ''}`}>Paid Out</button>
          <button onClick={() => setActiveView('on-hold')}
            className={`stl-tab ${activeView === 'on-hold' ? 'active' : ''}`}>
            On Hold {onHoldCount > 0 && <span className="stl-tab-badge stl-tab-badge-warning">{onHoldCount}</span>}
          </button>
          <button onClick={() => setActiveView('settings')}
            className={`stl-tab ${activeView === 'settings' ? 'active' : ''}`}>
            <Settings size={14} /> Settings
          </button>
        </div>

        {/* Right side: filters (only show when not settings view) */}
        {activeView !== 'settings' && (
          <div className="stl-filters">
            <div className="stl-search-input">
              <Search size={15} />
              <input type="text" value={merchantSearch} onChange={(e) => setMerchantSearch(e.target.value)} placeholder="Search merchant..." />
            </div>
            <select value={filters.status} onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}>
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSING">Processing</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
              <option value="ON_HOLD">On Hold</option>
            </select>
            <select value={providerFilter} onChange={(e) => { setProviderFilter(e.target.value); setPagination(prev => ({ ...prev, current: 1 })); }}>
              <option value="">All Gateways</option>
              <option value="PAYSTACK">Paystack</option>
              <option value="FLUTTERWAVE">Flutterwave</option>
              <option value="PAYSTACK_VIRTUAL_TERMINAL">Paystack Terminal</option>
              <option value="PAYSTACK_DVA">DVA (Bank Transfer)</option>
            </select>
            <select value={datePreset} onChange={(e) => handleDatePreset(e.target.value)}>
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
            {datePreset === 'custom' && (
              <div className="stl-date-inputs">
                <input type="date" value={filters.startDate} onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))} />
                <input type="date" value={filters.endDate} onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))} />
              </div>
            )}
            {(merchantSearch || filters.status || orderStatusFilter || providerFilter || filters.startDate || filters.endDate) && (
              <button onClick={clearFilters} className="stl-btn stl-btn-ghost stl-btn-sm">
                <X size={14} /> Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Settings Panel */}
      {activeView === 'settings' && (
        <div className="stl-settings-panel">
          <div className="stl-settings-header">
            <h2>Settlement Configuration</h2>
            <p>Configure hold settings, release conditions, and merchant notifications</p>
          </div>

          <div className="stl-settings-content">
            {/* Hold Status Banner */}
            <div className={`stl-settings-status ${settlementConfig.holdEnabled ? 'active' : 'inactive'}`}>
              <div className="stl-settings-status-icon">
                {settlementConfig.holdEnabled ? <Shield size={24} /> : <Pause size={24} />}
              </div>
              <div className="stl-settings-status-content">
                <strong>Settlement Hold is {settlementConfig.holdEnabled ? 'Active' : 'Disabled'}</strong>
                <span>{settlementConfig.holdEnabled
                  ? `New settlements are held for ${settlementConfig.holdPeriodHours} hours before release`
                  : 'Settlements are processed immediately without a hold period'}</span>
              </div>
            </div>

            {/* Hold Settings */}
            <div className="stl-settings-section">
              <h3><Shield size={18} /> Hold Settings</h3>
              <div className="stl-settings-row">
                <div className="stl-settings-label">
                  <div>
                    <h4>Enable Settlement Hold</h4>
                    <p>Hold new settlements before they become available for payout</p>
                  </div>
                </div>
                <label className="stl-toggle">
                  <input type="checkbox" checked={settlementConfig.holdEnabled} onChange={(e) => setSettlementConfig(prev => ({ ...prev, holdEnabled: e.target.checked }))} />
                  <span className="stl-toggle-slider"></span>
                </label>
              </div>

              {settlementConfig.holdEnabled && (
                <div className="stl-settings-row">
                  <div className="stl-settings-label">
                    <Timer size={18} />
                    <div>
                      <h4>Hold Period</h4>
                      <p>Duration before automatic release</p>
                    </div>
                  </div>
                  <div className="stl-hold-period">
                    <input type="number" value={settlementConfig.holdPeriodHours} onChange={(e) => setSettlementConfig(prev => ({ ...prev, holdPeriodHours: parseInt(e.target.value) || 48 }))} min="1" max="720" />
                    <span className="stl-hold-period-suffix">hours</span>
                    <span className="stl-hold-period-hint">({Math.floor(settlementConfig.holdPeriodHours / 24)}d {settlementConfig.holdPeriodHours % 24}h)</span>
                  </div>
                </div>
              )}
            </div>

            {/* Release Condition */}
            {settlementConfig.holdEnabled && (
              <div className="stl-settings-section">
                <h3><Clock size={18} /> Release Condition</h3>
                <p className="stl-settings-section-desc">When should held settlements be released?</p>
                <div className="stl-release-options">
                  {[
                    { value: 'TIME_BASED', label: 'Time Based Only', desc: `Release after ${settlementConfig.holdPeriodHours}h`, icon: <Timer size={20} /> },
                    { value: 'ORDER_DELIVERED', label: 'Order Delivered Only', desc: 'Release when order is delivered', icon: <Truck size={20} /> },
                    { value: 'TIME_OR_DELIVERED', label: 'Time OR Delivered', desc: 'Release when either condition is met', icon: <Play size={20} />, recommended: true }
                  ].map(opt => (
                    <label key={opt.value} className={`stl-release-option ${settlementConfig.releaseCondition === opt.value ? 'selected' : ''} ${opt.recommended ? 'recommended' : ''}`}>
                      <input type="radio" name="releaseCondition" value={opt.value} checked={settlementConfig.releaseCondition === opt.value} onChange={(e) => setSettlementConfig(prev => ({ ...prev, releaseCondition: e.target.value }))} />
                      {opt.recommended && <div className="stl-recommended-badge">Recommended</div>}
                      <div className="stl-release-option-icon">{opt.icon}</div>
                      <div className="stl-release-option-content">
                        <strong>{opt.label}</strong>
                        <span>{opt.desc}</span>
                      </div>
                      {settlementConfig.releaseCondition === opt.value && <CheckCircle2 size={18} className="stl-release-check" />}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Automation */}
            {settlementConfig.holdEnabled && (
              <div className="stl-settings-section">
                <h3><RefreshCw size={18} /> Automation</h3>
                <div className="stl-settings-row">
                  <div className="stl-settings-label">
                    <Play size={18} />
                    <div>
                      <h4>Auto-Release Expired Holds</h4>
                      <p>Automatically release when hold period expires (runs every 30 min)</p>
                    </div>
                  </div>
                  <label className="stl-toggle">
                    <input type="checkbox" checked={settlementConfig.autoReleaseEnabled} onChange={(e) => setSettlementConfig(prev => ({ ...prev, autoReleaseEnabled: e.target.checked }))} />
                    <span className="stl-toggle-slider"></span>
                  </label>
                </div>
              </div>
            )}

            {/* Notifications */}
            <div className="stl-settings-section">
              <h3><Bell size={18} /> Merchant Notifications</h3>
              <div className="stl-notification-cards">
                <div className={`stl-notification-card ${settlementConfig.notifyMerchantOnHold ? 'enabled' : ''}`}>
                  <div className="stl-notification-card-header">
                    <Shield size={18} />
                    <label className="stl-toggle small">
                      <input type="checkbox" checked={settlementConfig.notifyMerchantOnHold} onChange={(e) => setSettlementConfig(prev => ({ ...prev, notifyMerchantOnHold: e.target.checked }))} />
                      <span className="stl-toggle-slider"></span>
                    </label>
                  </div>
                  <h4>On Hold Notification</h4>
                  <p>Notify when settlement is placed on hold</p>
                </div>
                <div className={`stl-notification-card ${settlementConfig.notifyMerchantOnRelease ? 'enabled' : ''}`}>
                  <div className="stl-notification-card-header">
                    <CheckCircle2 size={18} />
                    <label className="stl-toggle small">
                      <input type="checkbox" checked={settlementConfig.notifyMerchantOnRelease} onChange={(e) => setSettlementConfig(prev => ({ ...prev, notifyMerchantOnRelease: e.target.checked }))} />
                      <span className="stl-toggle-slider"></span>
                    </label>
                  </div>
                  <h4>Release Notification</h4>
                  <p>Notify when settlement is released</p>
                </div>
                <div className={`stl-notification-card ${settlementConfig.notifyMerchantOnCreated ? 'enabled' : ''}`}>
                  <div className="stl-notification-card-header">
                    <CircleDollarSign size={18} />
                    <label className="stl-toggle small">
                      <input type="checkbox" checked={settlementConfig.notifyMerchantOnCreated} onChange={(e) => setSettlementConfig(prev => ({ ...prev, notifyMerchantOnCreated: e.target.checked }))} />
                      <span className="stl-toggle-slider"></span>
                    </label>
                  </div>
                  <h4>Created Notification</h4>
                  <p>Notify when new settlement is created</p>
                </div>
                <div className={`stl-notification-card ${settlementConfig.notifyMerchantOnAdjusted ? 'enabled' : ''}`}>
                  <div className="stl-notification-card-header">
                    <Pencil size={18} />
                    <label className="stl-toggle small">
                      <input type="checkbox" checked={settlementConfig.notifyMerchantOnAdjusted} onChange={(e) => setSettlementConfig(prev => ({ ...prev, notifyMerchantOnAdjusted: e.target.checked }))} />
                      <span className="stl-toggle-slider"></span>
                    </label>
                  </div>
                  <h4>Adjusted Notification</h4>
                  <p>Notify when settlement amount is adjusted</p>
                </div>
                <div className={`stl-notification-card ${settlementConfig.notifyMerchantOnFailed ? 'enabled' : ''}`}>
                  <div className="stl-notification-card-header">
                    <XCircle size={18} />
                    <label className="stl-toggle small">
                      <input type="checkbox" checked={settlementConfig.notifyMerchantOnFailed} onChange={(e) => setSettlementConfig(prev => ({ ...prev, notifyMerchantOnFailed: e.target.checked }))} />
                      <span className="stl-toggle-slider"></span>
                    </label>
                  </div>
                  <h4>Failed Notification</h4>
                  <p>Notify when payout fails</p>
                </div>
                <div className={`stl-notification-card ${settlementConfig.notifyMerchantOnPaidOut ? 'enabled' : ''}`}>
                  <div className="stl-notification-card-header">
                    <BanknoteIcon size={18} />
                    <label className="stl-toggle small">
                      <input type="checkbox" checked={settlementConfig.notifyMerchantOnPaidOut} onChange={(e) => setSettlementConfig(prev => ({ ...prev, notifyMerchantOnPaidOut: e.target.checked }))} />
                      <span className="stl-toggle-slider"></span>
                    </label>
                  </div>
                  <h4>Paid Out Notification</h4>
                  <p>Notify when funds are transferred</p>
                </div>
              </div>
            </div>

            <div className="stl-settings-actions">
              <button onClick={saveSettlementConfig} disabled={configLoading} className="stl-btn stl-btn-primary stl-btn-lg">
                {configLoading ? <><RefreshCw size={18} className="stl-spinner" /> Saving...</> : <><CheckCircle2 size={18} /> Save Configuration</>}
              </button>
            </div>
          </div>

          <div className="stl-settings-info">
            <Info size={16} />
            <span>These settings apply to new settlements only. Manual actions override automatic hold settings.</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      {activeView !== 'settings' && (
        <>
          {/* Table Header */}
          <div className="stl-table-container">
            <div className="stl-table-header">
              <div className="stl-table-header-left">
                <h3>{tableTitle}</h3>
                <span className="stl-table-count">{pagination.total}</span>
              </div>
              <div className="stl-table-actions">
                {activeView === 'payment-queue' && (
                  <button onClick={exportForPayment} className="stl-btn stl-btn-primary stl-btn-sm">
                    <Download size={14} /> Export for Payment
                  </button>
                )}
                <div className="stl-export-dropdown" ref={exportMenuRef}>
                  <button onClick={() => setShowExportMenu(!showExportMenu)} className="stl-btn stl-btn-secondary stl-btn-sm" disabled={filteredSettlements.length === 0}>
                    <FileDown size={14} /> Export
                  </button>
                  {showExportMenu && (
                    <div className="stl-export-menu">
                      <button onClick={() => { exportAllCSV(); setShowExportMenu(false); }}>
                        <FileDown size={14} /> Export CSV
                      </button>
                      <button onClick={() => { exportExcel(); setShowExportMenu(false); }}>
                        <FileSpreadsheet size={14} /> Export Excel (.xls)
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <div className="stl-loading">
                <RefreshCw size={28} className="stl-spinner" />
                <p>Loading settlements...</p>
              </div>
            ) : (
              <div className="stl-table-wrapper">
                <table className="stl-table">
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      {isPaymentQueue && <th>Bank</th>}
                      <th>Payout</th>
                      <th>Gateway</th>
                      <th>Order</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSettlements.length === 0 ? (
                      <tr>
                        <td colSpan={tableColSpan}>
                          <div className="stl-empty">
                            <CircleDollarSign size={40} strokeWidth={1.5} />
                            <p>No settlements found</p>
                            <span>{activeView === 'payment-queue' ? 'No settlements ready for payment' : 'Settlements will appear here once orders are placed'}</span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredSettlements.map((s) => (
                        <tr key={s.id} className={s.settlementStatus === 'PROCESSING' ? 'stl-row-highlight' : ''}>
                          {/* Merchant */}
                          <td>
                            <div className="stl-merchant-cell">
                              <span className="stl-merchant-name">{s.merchant?.store?.name || s.storeName || s.merchant?.username || 'N/A'}</span>
                              <span className="stl-merchant-email">{s.merchant?.email || ''}</span>
                            </div>
                          </td>
                          {/* Bank (payment queue only) */}
                          {isPaymentQueue && (
                            <td>
                              <div className="stl-bank-cell">
                                <span>{s.merchantBankDetails?.bankName || s.merchantBankDetails?.bank_name || 'N/A'}</span>
                                <span className="stl-account-num">{s.merchantBankDetails?.accountNumber || s.merchantBankDetails?.account_number || ''}</span>
                                <button className="stl-copy-btn" onClick={() => copyBankDetails(s)} title="Copy bank details">
                                  {copiedId === s.id ? <CheckCheck size={12} /> : <Copy size={12} />}
                                </button>
                              </div>
                            </td>
                          )}
                          {/* Payout */}
                          <td>
                            <span className="stl-payout-amount">{formatCurrency(s.merchantAmount)}</span>
                            <span className="stl-payout-sub">{formatCurrency(s.totalAmount)}</span>
                          </td>
                          {/* Gateway */}
                          <td>
                            {(() => {
                              const prov = s.metadata?.paymentProvider?.toUpperCase() || '';
                              if (prov.includes('FLUTTERWAVE')) return <span className="stl-provider-badge stl-provider-flutterwave">Flutterwave</span>;
                              if (prov.includes('VIRTUAL_TERMINAL')) return <span className="stl-provider-badge stl-provider-dva">Paystack Terminal</span>;
                              if (prov.includes('DVA') || prov.includes('PAYSTACK_DVA')) return <span className="stl-provider-badge stl-provider-dva">DVA</span>;
                              if (prov.includes('PAYSTACK')) return <span className="stl-provider-badge stl-provider-paystack">Paystack</span>;
                              return <span className="stl-provider-badge stl-provider-unknown">Unknown</span>;
                            })()}
                          </td>
                          {/* Order Status */}
                          <td>
                            {s.order?.status ? (
                              <span className="stl-order-status-mini" style={{ color: getOrderStatusColor(s.order.status) }}>
                                {s.order.status}
                              </span>
                            ) : (
                              <span className="stl-text-muted">&mdash;</span>
                            )}
                          </td>
                          {/* Status */}
                          <td>
                            <span className={`stl-status stl-status-${s.settlementStatus?.toLowerCase() || 'pending'}`}>
                              {getStatusIcon(s.settlementStatus)}
                              {s.settlementStatus?.replace('_', ' ') || 'Pending'}
                            </span>
                          </td>
                          {/* Date */}
                          <td>
                            <span className="stl-date">{formatShortDate(s.createdAt)}</span>
                          </td>
                          {/* Actions */}
                          <td>
                            <div className="stl-actions">
                              {s.settlementStatus === 'PENDING' && (
                                <>
                                  <button onClick={() => promptApprove(s.id)} className="stl-action-btn stl-action-approve" title="Approve"><CheckCircle2 size={15} /></button>
                                  <button onClick={() => promptHold(s.id)} className="stl-action-btn stl-action-hold" title="Hold"><Shield size={15} /></button>
                                </>
                              )}
                              {s.settlementStatus === 'PROCESSING' && (
                                <>
                                  <button onClick={() => promptMarkPaid(s)} className="stl-action-btn stl-action-paid" title="Mark as Paid"><CreditCard size={15} /></button>
                                  <button onClick={() => promptPayout(s)} className="stl-action-btn stl-action-payout" title="Auto Payout"><Send size={15} /></button>
                                </>
                              )}
                              {s.settlementStatus === 'FAILED' && !isSettlementBlockedByOrderStatus(s) && (
                                <>
                                  <button onClick={() => promptMarkPaid(s)} className="stl-action-btn stl-action-paid" title="Mark as Paid"><CreditCard size={15} /></button>
                                  <button onClick={() => promptPayout(s)} className="stl-action-btn stl-action-payout" title="Retry"><RotateCcw size={15} /></button>
                                </>
                              )}
                              {s.settlementStatus === 'ON_HOLD' && (
                                <button onClick={() => promptRelease(s.id)} className="stl-action-btn stl-action-approve" title="Release"><Play size={15} /></button>
                              )}
                              <button onClick={() => viewDetails(s)} className="stl-action-btn stl-action-view" title="View details"><Eye size={15} /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && pagination.total > 0 && (
              <div className="stl-pagination">
                <button onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))} disabled={pagination.current === 1}>
                  <ChevronLeft size={16} /> Previous
                </button>
                <div className="stl-pagination-info">
                  Page <strong>{pagination.current}</strong> of <strong>{pagination.pages}</strong>
                  <span className="stl-pagination-total">({pagination.total} settlements)</span>
                </div>
                <button onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))} disabled={pagination.current >= pagination.pages}>
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Detail Modal */}
      {showModal && selectedItem && (() => {
        const breakdown = getSettlementBreakdown(selectedItem);
        return (
        <div className="stl-modal-overlay" onClick={closeModal}>
          <div className="stl-modal stl-modal-lg stl-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="stl-modal-header">
              <div className="stl-modal-header-left">
                <h2>Settlement Details</h2>
                <span className="stl-modal-id">#{selectedItem.id?.slice(-12)}</span>
              </div>
              <button onClick={closeModal} className="stl-modal-close"><X size={20} /></button>
            </div>

            <div className="stl-modal-body">
              {/* Status Banner */}
              <div className={`stl-detail-status-banner stl-detail-status-${selectedItem.settlementStatus?.toLowerCase()}`}>
                <div className="stl-detail-status-left">
                  {getStatusIcon(selectedItem.settlementStatus)}
                  <div>
                    <strong style={{ display: 'block' }}>{selectedItem.settlementStatus?.replace('_', ' ') || 'Pending'}</strong>
                    <span style={{ display: 'block', fontSize: '0.8125rem', color: '#6b7280', marginTop: '2px' }}>{getSettlementStatusReason(selectedItem)}</span>
                  </div>
                </div>
                <div className="stl-detail-status-amount">
                  {formatCurrency(breakdown.merchantPayout)}
                </div>
              </div>

              {/* Financial Breakdown */}
              <div className="stl-detail-section">
                <h3><CircleDollarSign size={16} /> Financial Breakdown</h3>
                <div className="stl-detail-finance">
                  {breakdown.hasOrderParts && (
                    <>
                      <div className="stl-finance-row">
                        <span>Products subtotal</span>
                        <span>{formatCurrency(breakdown.subtotal)}</span>
                      </div>
                      <div className="stl-finance-row">
                        <span>VAT</span>
                        <span>{formatCurrency(breakdown.vat)}</span>
                      </div>
                      <div className="stl-finance-row">
                        <span>Delivery</span>
                        <span>{formatCurrency(breakdown.delivery)}</span>
                      </div>
                    </>
                  )}
                  <div className="stl-finance-row stl-finance-subtotal">
                    <span>Customer-paid order total</span>
                    <span>{formatCurrency(breakdown.customerPaidTotal)}</span>
                  </div>
                  {breakdown.serviceCharge > 0 && (
                    <div className={`stl-finance-row ${breakdown.serviceChargeDeductedFromPayout ? 'stl-finance-deduct' : 'stl-finance-muted'}`}>
                      <span>{breakdown.serviceChargeLabel}</span>
                      <span>{breakdown.serviceChargeDeductedFromPayout ? '- ' : ''}{formatCurrency(breakdown.serviceCharge)}</span>
                    </div>
                  )}
                  {breakdown.feeSplit?.hasSplit && (
                    <>
                      <div className="stl-finance-row stl-finance-muted">
                        <span>Processor recovery{breakdown.feeSplit.provider ? ` (${breakdown.feeSplit.provider})` : ''}</span>
                        <span>{formatCurrency(breakdown.feeSplit.processorRecovery)}</span>
                      </div>
                      <div className="stl-finance-row stl-finance-muted">
                        <span>Revstar fee</span>
                        <span>{formatCurrency(breakdown.feeSplit.platformFee)}</span>
                      </div>
                      <div className="stl-finance-row stl-finance-muted">
                        <span>Platform VAT</span>
                        <span>{formatCurrency(breakdown.feeSplit.platformVat)}</span>
                      </div>
                      {breakdown.feeSplit.profitability && (
                        <>
                          <div className="stl-finance-row stl-finance-muted">
                            <span>Estimated gateway fee</span>
                            <span>{formatCurrency(breakdown.feeSplit.profitability.estimatedGatewayFeeAmount)}</span>
                          </div>
                          {moneyValue(breakdown.feeSplit.profitability.processorRecoveryShortfall) > 0 && (
                            <div className="stl-finance-row stl-finance-deduct">
                              <span>Processor recovery shortfall</span>
                              <span>- {formatCurrency(breakdown.feeSplit.profitability.processorRecoveryShortfall)}</span>
                            </div>
                          )}
                          {moneyValue(breakdown.feeSplit.profitability.processorRecoverySurplus) > 0 && (
                            <div className="stl-finance-row stl-finance-muted">
                              <span>Processor recovery surplus</span>
                              <span>{formatCurrency(breakdown.feeSplit.profitability.processorRecoverySurplus)}</span>
                            </div>
                          )}
                          <div className="stl-finance-row stl-finance-muted">
                            <span>Estimated Revstar net margin</span>
                            <span>{formatCurrency(breakdown.feeSplit.profitability.estimatedRevstarNetMargin)}</span>
                          </div>
                        </>
                      )}
                      <div className="stl-finance-row stl-finance-muted">
                        <span>Fee model</span>
                        <span>{breakdown.feeSplit.modelVersion ? `v${breakdown.feeSplit.modelVersion}` : 'v2'}{breakdown.feeSplit.whoPays ? ` · ${String(breakdown.feeSplit.whoPays).toLowerCase()} pays` : ''}</span>
                      </div>
                    </>
                  )}
                  <div className="stl-finance-row stl-finance-total">
                    <span>Merchant Payout</span>
                    <span>{formatCurrency(breakdown.merchantPayout)}</span>
                  </div>
                </div>
              </div>

              {/* Order & Transaction Information */}
              {(selectedItem.order || selectedItem.orderId || selectedItem.transactionId) && (
                <div className="stl-detail-section">
                  <h3><Package size={16} /> {selectedItem.order ? 'Order Information' : 'Transaction Information'}</h3>
                  <div className="stl-detail-grid">
                    {(selectedItem.order?.orderNumber || selectedItem.orderId) && (
                      <div className="stl-detail-item">
                        <label>{selectedItem.order?.orderNumber ? 'Order #' : 'Order ID'}</label>
                        <span className="stl-detail-code" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                          {selectedItem.order?.orderNumber || selectedItem.orderId}
                        </span>
                      </div>
                    )}
                    <div className="stl-detail-item">
                      <label>Order Status</label>
                      {selectedItem.order?.status ? (
                        <span className="stl-order-status-badge" style={{ color: getOrderStatusColor(selectedItem.order.status), borderColor: getOrderStatusColor(selectedItem.order.status) }}>
                          {selectedItem.order.status}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                          {selectedItem.orderId ? 'Order data unavailable' : 'No linked order'}
                        </span>
                      )}
                    </div>
                    {selectedItem.order?.paymentStatus && (
                      <div className="stl-detail-item">
                        <label>Payment Status</label>
                        <span style={{
                          color: selectedItem.order.paymentStatus === 'PAID' ? '#059669' :
                                 selectedItem.order.paymentStatus === 'UNPAID' ? '#dc2626' : '#d97706',
                          fontWeight: 600
                        }}>
                          {selectedItem.order.paymentStatus}
                        </span>
                      </div>
                    )}
                    <div className="stl-detail-item">
                      <label>Order Date</label>
                      <span>{selectedItem.order?.createdAt ? formatDate(selectedItem.order.createdAt) : (
                        <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>{'\u2014'}</span>
                      )}</span>
                    </div>
                    {selectedItem.order?.channel && (
                      <div className="stl-detail-item">
                        <label>Channel</label>
                        <span>{selectedItem.order.channel === 'STOREFRONT' ? 'Storefront' : selectedItem.order.channel === 'POS' ? 'POS' : selectedItem.order.channel === 'WHATSAPP' ? 'WhatsApp' : selectedItem.order.channel === 'MANUAL' ? 'Manual' : selectedItem.order.channel}</span>
                      </div>
                    )}
                    <div className="stl-detail-item">
                      <label>Payment Ref</label>
                      <span className="stl-detail-code" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                        {selectedItem.order?.paymentReference || selectedItem.transactionId || '\u2014'}
                      </span>
                    </div>
                    {selectedItem.createdBy && (
                      <div className="stl-detail-item">
                        <label>Source</label>
                        <span>
                          {(() => {
                            const source = selectedItem.metadata?.paymentProvider || selectedItem.createdBy;
                            if (source === 'PAYSTACK_VIRTUAL_TERMINAL') return 'Paystack Terminal';
                            if (source === 'PAYSTACK_DVA') return 'Bank Transfer (DVA)';
                            if (source === 'SYSTEM') return 'Payment Gateway';
                            return source;
                          })()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Order Status Timeline */}
                  {selectedItem.order?.status && (
                    <div className="stl-timeline-section">
                      <label>Order Progression</label>
                      {renderOrderTimeline(selectedItem.order)}
                    </div>
                  )}

                  {/* Info for platform-held bank-transfer settlements without linked orders */}
                  {!selectedItem.order && !selectedItem.orderId && ['PAYSTACK_DVA', 'PAYSTACK_VIRTUAL_TERMINAL'].includes(selectedItem.metadata?.paymentProvider || selectedItem.createdBy) && (
                    <div className="stl-detail-info" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 12px', background: '#eff6ff', borderRadius: '8px', marginTop: '12px', fontSize: '0.8125rem', color: '#3b82f6' }}>
                      <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                      <span>This settlement was created from a direct {selectedItem.metadata?.paymentProvider === 'PAYSTACK_VIRTUAL_TERMINAL' ? 'Paystack Terminal' : 'legacy DVA'} transfer and is not linked to a specific order.</span>
                    </div>
                  )}

                  {/* Safety Warning for non-delivered orders */}
                  {selectedItem.order?.status && !['DELIVERED'].includes(selectedItem.order.status?.toUpperCase()) && selectedItem.settlementStatus !== 'COMPLETED' && (
                    <div className="stl-detail-warning">
                      <AlertCircle size={16} />
                      <span>
                        This order has <strong>not been delivered</strong> yet (Status: {selectedItem.order.status}).
                        {['CANCELLED', 'REFUNDED'].includes(selectedItem.order.status?.toUpperCase())
                          ? ' Settlement should NOT be processed.'
                          : ' Consider waiting for delivery confirmation before processing settlement.'}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Merchant + Bank Details Side-by-Side */}
              <div className="stl-detail-2col">
                <div className="stl-detail-section">
                  <h3><User size={16} /> Merchant</h3>
                  <div className="stl-detail-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div className="stl-detail-item">
                      <label>Name</label>
                      <span>{selectedItem.merchant?.username || 'N/A'}</span>
                    </div>
                    <div className="stl-detail-item">
                      <label>Email</label>
                      <span>{selectedItem.merchant?.email || 'N/A'}</span>
                    </div>
                    <div className="stl-detail-item">
                      <label>Store</label>
                      <span>{selectedItem.merchant?.store?.name || selectedItem.storeName || 'N/A'}</span>
                    </div>
                  </div>
                </div>
                <div className="stl-detail-section">
                  <h3><Building2 size={16} /> Bank Details</h3>
                  <div className="stl-detail-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div className="stl-detail-item">
                      <label>Bank</label>
                      <span>{selectedItem.merchantBankDetails?.bankName || selectedItem.merchantBankDetails?.bank_name || 'N/A'}</span>
                    </div>
                    <div className="stl-detail-item">
                      <label>Account</label>
                      <span className="stl-detail-code">{selectedItem.merchantBankDetails?.accountNumber || selectedItem.merchantBankDetails?.account_number || 'N/A'}</span>
                    </div>
                    <div className="stl-detail-item">
                      <label>Name</label>
                      <span>{selectedItem.merchantBankDetails?.accountName || selectedItem.merchantBankDetails?.account_name || 'N/A'}</span>
                    </div>
                  </div>
                  <button onClick={() => copyBankDetails(selectedItem)} className="stl-btn stl-btn-secondary stl-btn-sm" style={{ marginTop: '12px' }}>
                    {copiedId === selectedItem.id ? <><CheckCheck size={14} /> Copied!</> : <><Copy size={14} /> Copy Details</>}
                  </button>
                </div>
              </div>

              {/* Settlement Timeline */}
              <div className="stl-detail-section stl-detail-section-muted">
                <h3><Clock size={16} /> Timeline</h3>
                <div className="stl-audit-timeline">
                  <div className="stl-audit-item">
                    <div className="stl-audit-dot" />
                    <div className="stl-audit-content">
                      <strong>Settlement Created</strong>
                      <span>{formatDate(selectedItem.createdAt)}</span>
                    </div>
                  </div>
                  {selectedItem.holdUntil && (
                    <div className="stl-audit-item">
                      <div className="stl-audit-dot stl-audit-dot-warning" />
                      <div className="stl-audit-content">
                        <strong>Hold Until</strong>
                        <span>{formatDate(selectedItem.holdUntil)}</span>
                      </div>
                    </div>
                  )}
                  {selectedItem.holdReleasedAt && (
                    <div className="stl-audit-item">
                      <div className="stl-audit-dot stl-audit-dot-success" />
                      <div className="stl-audit-content">
                        <strong>Hold Released</strong>
                        <span>{formatDate(selectedItem.holdReleasedAt)}{selectedItem.holdReleasedBy ? ` by ${selectedItem.holdReleasedBy}` : ''}</span>
                      </div>
                    </div>
                  )}
                  {selectedItem.processedAt && (
                    <div className="stl-audit-item">
                      <div className="stl-audit-dot stl-audit-dot-success" />
                      <div className="stl-audit-content">
                        <strong>Processed</strong>
                        <span>{formatDate(selectedItem.processedAt)}{selectedItem.processedBy ? ` by ${selectedItem.processedBy}` : ''}</span>
                      </div>
                    </div>
                  )}
                  {selectedItem.payoutDate && (
                    <div className="stl-audit-item">
                      <div className="stl-audit-dot stl-audit-dot-success" />
                      <div className="stl-audit-content">
                        <strong>Payout Completed</strong>
                        <span>{formatDate(selectedItem.payoutDate)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Confirmation (if completed via manual payment) */}
              {selectedItem.metadata?.manualPayment && (
                <div className="stl-detail-section stl-detail-section-success">
                  <h3><CreditCard size={16} /> Payment Confirmation</h3>
                  <div className="stl-detail-grid">
                    <div className="stl-detail-item">
                      <label>Bank Reference</label>
                      <span className="stl-detail-code">{selectedItem.metadata?.bankTransferReference || 'N/A'}</span>
                    </div>
                    <div className="stl-detail-item">
                      <label>Transfer Date</label>
                      <span>{selectedItem.metadata?.bankTransferDate || 'N/A'}</span>
                    </div>
                    <div className="stl-detail-item">
                      <label>Marked By</label>
                      <span>{selectedItem.metadata?.markedPaidBy || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Failure Info */}
              {selectedItem.failureReason && (
                <div className="stl-detail-section stl-detail-section-error">
                  <h3><XCircle size={16} /> Failure Information</h3>
                  <p>{selectedItem.failureReason}</p>
                  {selectedItem.retryCount > 0 && <p className="stl-detail-retry">Retry attempts: {selectedItem.retryCount}</p>}
                </div>
              )}
            </div>

            {/* Modal Footer Actions */}
            <div className="stl-modal-footer">
              {selectedItem.settlementStatus === 'PROCESSING' && (
                <>
                  <button onClick={() => { closeModal(); promptMarkPaid(selectedItem); }} className="stl-btn stl-btn-success">
                    <CreditCard size={16} /> Mark as Paid
                  </button>
                  <button onClick={() => { closeModal(); promptPayout(selectedItem); }} className="stl-btn stl-btn-primary">
                    <Send size={16} /> Auto Payout
                  </button>
                </>
              )}
              {selectedItem.settlementStatus === 'PENDING' && (
                <>
                  <button onClick={() => { closeModal(); promptApprove(selectedItem.id); }} className="stl-btn stl-btn-success">
                    <CheckCircle2 size={16} /> Approve
                  </button>
                  <button onClick={() => { closeModal(); promptHold(selectedItem.id); }} className="stl-btn stl-btn-warning">
                    <Shield size={16} /> Hold
                  </button>
                </>
              )}
              {selectedItem.settlementStatus === 'ON_HOLD' && (
                <button onClick={() => { closeModal(); promptRelease(selectedItem.id); }} className="stl-btn stl-btn-success">
                  <Play size={16} /> Release
                </button>
              )}
              {selectedItem.settlementStatus === 'FAILED' && (
                isSettlementBlockedByOrderStatus(selectedItem) ? (
                  <span className="stl-modal-action-note">Payout retry is blocked because this order is refunded/cancelled.</span>
                ) : (
                  <button onClick={() => { closeModal(); promptPayout(selectedItem); }} className="stl-btn stl-btn-primary">
                    <RotateCcw size={16} /> Retry Payout
                  </button>
                )
              )}
              <button onClick={closeModal} className="stl-btn stl-btn-secondary">Close</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Confirmation Dialog */}
      {showConfirmDialog && confirmAction && (
        <div className="stl-modal-overlay" onClick={cancelAction}>
          <div className="stl-modal stl-modal-sm stl-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="stl-modal-header">
              <h2>{confirmAction.title}</h2>
              <button onClick={cancelAction} className="stl-modal-close"><X size={20} /></button>
            </div>
            <div className="stl-modal-body">
              <p className="stl-confirm-message">{confirmAction.message}</p>

              {confirmAction.action === 'adjust' && (
                <div className="stl-form-group">
                  <label>New Amount (NGN) <span className="stl-required">*</span></label>
                  <input type="number" value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} placeholder="Enter new amount" min="0" step="0.01" className="stl-input" />
                </div>
              )}

              {confirmAction.action === 'mark-paid' && (
                <>
                  <div className="stl-info-box">
                    <Info size={16} />
                    <span>Use this after completing a manual bank transfer to the merchant.</span>
                  </div>
                  <div className="stl-form-group">
                    <label>Bank Transfer Reference <span className="stl-required">*</span></label>
                    <input type="text" value={bankReference} onChange={(e) => setBankReference(e.target.value)} placeholder="e.g., NIP/123456789" className="stl-input" />
                  </div>
                  <div className="stl-form-group">
                    <label>Transfer Date</label>
                    <input type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} className="stl-input" />
                  </div>
                </>
              )}

              {confirmAction.action !== 'payout' && (
                <div className="stl-form-group">
                  <label>
                    {['hold', 'adjust'].includes(confirmAction.action) ? 'Reason (required)' : confirmAction.action === 'release' ? 'Release note (optional)' : 'Notes (optional)'}
                  </label>
                  <textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)} placeholder={confirmAction.action === 'hold' ? 'Why is this settlement being held?' : confirmAction.action === 'adjust' ? 'Reason for adjustment...' : confirmAction.action === 'release' ? 'Optional note for the release audit trail...' : 'Add notes...'} rows="3" className="stl-textarea" />
                </div>
              )}

              {confirmAction.action === 'payout' && (
                <>
                  <div className="stl-form-group">
                    <label>Payment Gateway</label>
                    <select
                      value={payoutProvider}
                      onChange={(e) => { setPayoutProvider(e.target.value); setPayoutError(null); }}
                      className="stl-select"
                    >
                      <option value="PAYSTACK">Paystack</option>
                      <option value="FLUTTERWAVE">Flutterwave</option>
                      <option value="PAYSTACK_VIRTUAL_TERMINAL">Paystack Terminal</option>
                      <option value="PAYSTACK_DVA">DVA (Bank Transfer)</option>
                    </select>
                    <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Select the gateway that received the original payment
                    </small>
                  </div>

                  {/* Insufficient Balance Error Display */}
                  {payoutError?.code === 'INSUFFICIENT_BALANCE' && (
                    <div className="stl-insufficient-balance-error">
                      <div className="stl-error-header">
                        <AlertCircle size={20} />
                        <strong>Insufficient {payoutError.details.provider} Balance</strong>
                      </div>

                      <div className="stl-error-amounts">
                        <div className="stl-error-row">
                          <span>Available Balance:</span>
                          <span>{formatCurrency(payoutError.details.availableBalance)}</span>
                        </div>
                        <div className="stl-error-row">
                          <span>Payout Amount:</span>
                          <span>{formatCurrency(payoutError.details.requiredAmount)}</span>
                        </div>
                        <div className="stl-error-row">
                          <span>Transfer Fee:</span>
                          <span>{formatCurrency(payoutError.details.transferFee)}</span>
                        </div>
                        <div className="stl-error-row stl-error-total">
                          <span>Total Required:</span>
                          <span>{formatCurrency(payoutError.details.totalRequired)}</span>
                        </div>
                        <div className="stl-error-row stl-error-shortfall">
                          <span>Shortfall:</span>
                          <span className="stl-text-danger">{formatCurrency(payoutError.details.shortfall)}</span>
                        </div>
                      </div>

                      {payoutError.details.settlementInfo && (
                        <div className="stl-error-schedule-info">
                          <strong>When will funds be available?</strong>
                          <p>{payoutError.details.settlementInfo.note}</p>
                          <ul>
                            <li>Local payments: {payoutError.details.settlementInfo.localSettlement}</li>
                            {payoutError.details.settlementInfo.internationalSettlement && (
                              <li>International: {payoutError.details.settlementInfo.internationalSettlement}</li>
                            )}
                            {payoutError.details.settlementInfo.payoutTime && (
                              <li>Payout time: {payoutError.details.settlementInfo.payoutTime}</li>
                            )}
                          </ul>
                          <p className="stl-tip"><strong>Tip:</strong> {payoutError.details.settlementInfo.tip}</p>
                        </div>
                      )}

                      {payoutError.details.ledgerBalance > 0 && (
                        <div className="stl-ledger-info">
                          <Info size={14} />
                          <span>Ledger Balance: {formatCurrency(payoutError.details.ledgerBalance)} (includes pending/unsettled funds)</span>
                        </div>
                      )}
                    </div>
                  )}

                  {!payoutError && (
                    <div className="stl-warning-box">
                      <AlertCircle size={16} />
                      <div>
                        <strong>This action will:</strong>
                        <ul>
                          <li>Deduct funds from your {payoutProvider === 'FLUTTERWAVE' ? 'Flutterwave' : 'Paystack'} balance</li>
                          <li>Transfer money to merchant's bank account</li>
                          <li>Mark settlement as COMPLETED</li>
                        </ul>
                        <p>This cannot be undone.</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="stl-modal-footer">
              <button onClick={cancelAction} className="stl-btn stl-btn-secondary" disabled={processing}>Cancel</button>
              <button onClick={executeAction} className={`stl-btn ${confirmAction.confirmClass}`} disabled={processing}>
                {processing ? <><RefreshCw size={14} className="stl-spinner" /> Processing...</> : confirmAction.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settlements;
